// Generic per-step "status update" composer. CA writes a short message
// (e.g. "Filed with portal, ack number 1234") and clicks Save — the client
// sees it on the matching step. Independent from internal notes (addNote)
// because this is client-visible.

import { CheckCircle2, History, MessageSquare, Save } from "lucide-react";
import { useEffect, useState } from "react";
import API from "../../lib/api";

// Mirrors the backend allowlist in adminService.updateStepStatusMessage.
export const STATE_OPTIONS = [
  {
    value: "not_started",
    label: "Not Started",
    pill: "bg-gray-100 text-gray-600",
    dot: "#9CA3AF",
  },
  {
    value: "in_progress",
    label: "Working on it",
    pill: "bg-amber-100 text-amber-700",
    dot: "#F59E0B",
  },
  {
    value: "awaiting",
    label: "Awaiting reply",
    pill: "bg-sky-100 text-sky-700",
    dot: "#0EA5E9",
  },
  {
    value: "on_hold",
    label: "On hold",
    pill: "bg-orange-100 text-orange-700",
    dot: "#FB923C",
  },
  {
    value: "completed",
    label: "Completed",
    pill: "bg-green-100 text-green-700",
    dot: "#10B981",
  },
];
const STATE_BY_VALUE = Object.fromEntries(
  STATE_OPTIONS.map((o) => [o.value, o]),
);
export const stateMeta = (v) => STATE_BY_VALUE[v] || null;

function timeAgo(ts) {
  if (!ts) return "";
  const sec = Math.max(
    1,
    Math.round((Date.now() - new Date(ts).getTime()) / 1000),
  );
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)} min ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)} h ago`;
  return new Date(ts).toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export default function StepStatusPanel({
  orderId,
  stepNumber,
  stepName,
  initial,
  onRefresh,
}) {
  const [message, setMessage] = useState(initial?.status_message || "");
  const [stateValue, setStateValue] = useState(
    initial?.status_state || "not_started",
  );
  const [lastSaved, setLastSaved] = useState(
    initial?.status_updated_at || null,
  );
  const [lastSavedBy, setLastSavedBy] = useState(
    initial?.status_updated_by_name || null,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [justSaved, setJustSaved] = useState(false);
  const [history, setHistory] = useState(initial?.status_history || []);

  useEffect(() => {
    setMessage(initial?.status_message || "");
    setStateValue(initial?.status_state || "not_started");
    setLastSaved(initial?.status_updated_at || null);
    setLastSavedBy(initial?.status_updated_by_name || null);
    setHistory(
      Array.isArray(initial?.status_history) ? initial.status_history : [],
    );
  }, [
    initial?.status_message,
    initial?.status_state,
    initial?.status_updated_at,
    initial?.status_updated_by_name,
    initial?.status_history,
  ]);

  const onSave = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await API.put(
        `/admin/orders/${orderId}/steps/${stepNumber}/status`,
        { message: message.trim().slice(0, 1000), state: stateValue },
      );
      setLastSaved(data.status_updated_at);
      setLastSavedBy(data.status_updated_by);
      if (Array.isArray(data.status_history)) setHistory(data.status_history);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2000);
      if (onRefresh) await onRefresh();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to save status");
    }
    setSaving(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <MessageSquare className="h-4 w-4 text-[#6C63FF]" />
        <h4 className="text-sm font-heading font-bold text-[#1A1A2E]">
          {stepName} — Client-Visible Status
        </h4>
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E6F8] p-4 space-y-3">
        <p className="text-[11px] text-[#6B7280] font-body">
          The selected state + message appears to the client on their {stepName}{" "}
          step page. Pick a state so they know exactly where things stand.
        </p>

        {/* State picker */}
        <div>
          <p className="text-[10px] font-heading font-bold uppercase tracking-wider text-[#9CA3AF] mb-1.5">
            Status
          </p>
          <div className="flex flex-wrap gap-1.5">
            {STATE_OPTIONS.map((o) => {
              const active = stateValue === o.value;
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => setStateValue(o.value)}
                  className={`inline-flex items-center gap-1.5 text-[11px] font-body font-semibold rounded-full px-3 py-1 border transition-colors ${
                    active
                      ? `${o.pill} border-transparent ring-2 ring-[#6C63FF]/40`
                      : "bg-white text-[#6B7280] border-[#E8E6F8] hover:bg-[#F5F4FB]"
                  }`}
                >
                  <span
                    className="inline-block w-2 h-2 rounded-full"
                    style={{ background: o.dot }}
                  />
                  {o.label}
                </button>
              );
            })}
          </div>
        </div>

        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, 1000))}
          rows={3}
          placeholder={`Update the client on the ${stepName} progress…`}
          className="w-full text-sm font-body border border-[#E8E6F8] rounded-lg p-2.5 bg-[#F5F4FB] focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50 resize-none"
        />
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 text-[10px] text-[#9CA3AF] font-body">
            <span>{message.length}/1000</span>
            {lastSaved && (
              <>
                <span>·</span>
                <span>
                  Last updated by {lastSavedBy || "you"} · {timeAgo(lastSaved)}
                </span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            {justSaved && (
              <span className="inline-flex items-center gap-1 text-[10px] font-body font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                <CheckCircle2 className="h-3 w-3" /> Saved
              </span>
            )}
            <button
              onClick={onSave}
              disabled={saving}
              className="inline-flex items-center gap-1.5 text-xs font-body font-semibold bg-[#6C63FF] hover:bg-[#5B52CC] disabled:opacity-50 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <Save className="h-3.5 w-3.5" />
              {saving ? "Saving…" : "Send to Client"}
            </button>
          </div>
        </div>
        {error && <p className="text-[11px] text-red-600 font-body">{error}</p>}
      </div>

      {/* Full update history — newest first */}
      {history.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#E8E6F8] p-4">
          <div className="flex items-center gap-2 mb-3">
            <History className="h-4 w-4 text-[#6C63FF]" />
            <h4 className="text-xs font-heading font-bold uppercase tracking-wider text-[#6B7280]">
              Update History ({history.length})
            </h4>
          </div>
          <ol className="relative border-l border-[#E8E6F8] ml-1 space-y-4">
            {[...history].reverse().map((h, i) => {
              const meta = stateMeta(h.state);
              return (
                <li key={`${h.at}-${i}`} className="ml-4">
                  <span
                    className="absolute -left-[5px] mt-1 w-2.5 h-2.5 rounded-full ring-2 ring-white"
                    style={{ background: meta?.dot || "#9CA3AF" }}
                  />
                  <div className="flex items-center gap-2 flex-wrap">
                    {meta && (
                      <span
                        className={`inline-flex items-center gap-1 text-[10px] font-body font-semibold rounded-full px-2 py-0.5 ${meta.pill}`}
                      >
                        <span
                          className="inline-block w-1.5 h-1.5 rounded-full"
                          style={{ background: meta.dot }}
                        />
                        {meta.label}
                      </span>
                    )}
                    <span className="text-[10px] text-[#9CA3AF] font-body">
                      {h.at
                        ? `${new Date(h.at).toLocaleString("en-IN", {
                            timeZone: "Asia/Kolkata",
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                            hour: "numeric",
                            minute: "2-digit",
                            second: "2-digit",
                            hour12: true,
                          })} IST`
                        : ""}
                      {h.by ? ` · by ${h.by}` : ""}
                    </span>
                  </div>
                  {h.message && (
                    <p className="text-xs text-[#1A1A2E] font-body mt-1 whitespace-pre-wrap break-words">
                      {h.message}
                    </p>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </div>
  );
}
